import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertCircle, ArrowRight, ArrowLeft } from "lucide-react";
import { BrandMark } from "@/components/Brand";

const G = "#C9A24A";
const serif = "font-['Instrument_Serif',serif] font-normal";
const field = "mt-2 h-12 rounded-none border-[#C9A24A]/25 bg-[#12100B] text-[#F5EEDC] placeholder:text-[#F5EEDC]/25 focus-visible:ring-1 focus-visible:ring-[#C9A24A] focus-visible:ring-offset-0 focus-visible:border-[#C9A24A]";
const label = "text-[10px] tracking-[0.3em] text-[#F5EEDC]/55";

const ArtPanel = () => (
  <div className="relative hidden lg:flex lg:w-1/2 flex-col justify-end overflow-hidden border-r border-[#C9A24A]/20 p-14">
    <motion.img src="/landing/gold-art.webp" alt="" initial={{ scale: 1.2 }} animate={{ scale: 1.05 }} transition={{ duration: 16, ease: "easeOut" }} className="absolute inset-0 h-full w-full object-cover opacity-60" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0907] via-[#0A0907]/60 to-[#0A0907]/20" />
    <div className="relative">
      <div className="text-[11px] tracking-[0.4em]" style={{ color: G }}>MADE BY DESK INSTITUTIONAL FLOW TRADERS</div>
      <h2 className={`${serif} mt-5 text-6xl leading-[0.95] text-[#F5EEDC]`}>The scanner institutional desks <em style={{ color: G }}>keep private.</em></h2>
    </div>
  </div>
);

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { user, login, register, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = isLogin ? await login(email, password) : await register(name, email, password);
    setLoading(false);
    if (res.ok) navigate("/app/scanner");
    else setError(res.error);
  };

  return (
    <div className="tfr-landing min-h-screen bg-[#0A0907] text-[#F5EEDC] flex" data-testid="auth-page">
      <ArtPanel />
      <div className="flex w-full lg:w-1/2 flex-col px-6 sm:px-12">
        <div className="flex h-24 items-center justify-between">
          <Link to="/" data-testid="auth-home"><BrandMark className="h-11 w-11" /></Link>
          <Link to="/" className="group flex items-center gap-2 text-xs tracking-[0.3em] text-[#F5EEDC]/55 hover:text-[#F5EEDC]" data-testid="auth-back">
            <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-1" />HOME
          </Link>
        </div>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="mx-auto my-auto w-full max-w-md py-12">
          <div className="text-[11px] tracking-[0.4em]" style={{ color: G }}>{isLogin ? "MEMBER ACCESS" : "REQUEST ACCESS"}</div>
          <h1 className={`${serif} mt-4 text-5xl sm:text-6xl leading-none`}>{isLogin ? <>Welcome <em style={{ color: G }}>back.</em></> : <>Join the <em style={{ color: G }}>desk.</em></>}</h1>
          <p className="mt-4 text-[#F5EEDC]/55">{isLogin ? "Sign in to your TFR scanner." : "Create your account — the TFR desk activates your access."}</p>

          {isLogin && user && (
            <div className="mt-6 border border-[#C9A24A]/30 bg-[#12100B] p-5" data-testid="auth-signed-in">
              <div className="text-sm text-[#F5EEDC]/70">You're already signed in as <span className="text-[#F5EEDC]">{user.email}</span>.</div>
              <div className="mt-4 flex flex-wrap gap-3">
                <button onClick={() => navigate("/app/today")} className="px-5 py-2.5 text-[11px] font-semibold tracking-[0.25em] text-[#0A0907] hover:brightness-110" style={{ background: G }} data-testid="auth-continue">CONTINUE TO SCANNER</button>
                <button onClick={logout} className="border border-[#C9A24A]/40 px-5 py-2.5 text-[11px] tracking-[0.25em] text-[#F5EEDC]/75 hover:text-[#F5EEDC]" data-testid="auth-use-other">USE ANOTHER ACCOUNT</button>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-6 flex items-center gap-2 border border-[#E8A0A0]/40 bg-[#E8A0A0]/10 px-3 py-2.5 text-sm text-[#F2C4C4]" data-testid="auth-error">
              <AlertCircle className="h-4 w-4 shrink-0" /> {error}
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-5">
            {!isLogin && (
              <div>
                <Label className={label}>NAME</Label>
                <Input data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} required className={field} placeholder="Your name" />
              </div>
            )}
            <div>
              <Label className={label}>EMAIL</Label>
              <Input data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={field} placeholder="you@example.com" />
            </div>
            <div>
              <Label className={label}>PASSWORD</Label>
              <Input data-testid="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className={field} placeholder="••••••••" />
            </div>
            <button data-testid="auth-submit" type="submit" disabled={loading}
              className="group mt-2 flex h-12 w-full items-center justify-center text-xs font-semibold tracking-[0.35em] text-[#0A0907] transition-[filter,box-shadow] hover:brightness-110 hover:shadow-[0_0_50px_-12px_#C9A24A] disabled:opacity-60" style={{ background: G }}>
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {isLogin ? "SIGN IN" : "CREATE ACCOUNT"}
              {!loading && <ArrowRight className="ml-3 h-4 w-4 transition-transform group-hover:translate-x-1" />}
            </button>
          </form>

          <p className="mt-8 border-t border-[#C9A24A]/15 pt-6 text-sm text-[#F5EEDC]/55">
            {isLogin ? "New here? " : "Already a member? "}
            <Link to={isLogin ? "/register" : "/login"} className="underline underline-offset-4 hover:no-underline" style={{ color: G }} data-testid="auth-switch">
              {isLogin ? "Create an account" : "Sign in"}
            </Link>
          </p>
        </motion.div>
        <p className="pb-6 text-center font-mono text-[10.5px] text-[#F5EEDC]/35">
          Analytics tool only. Not SEBI-registered advice. F&amp;O trading carries high risk.
        </p>
      </div>
    </div>
  );
}
